import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
  UncontrolledButtonDropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  ButtonGroup,
  Button,
} from "reactstrap";
import {
  convertFromRaw,
  convertToRaw,
  Editor,
  EditorState,
  RichUtils,
  ContentState,
} from "draft-js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "react-select";
import AdrLocMiscMaterialsV3 from "./AdrLocMiscMaterialsV3";
import AlertModal from "../_common/AlertModal";
import { ADR_EDIT } from "../../_constants/actionConstants";
import AdrLocTestsPerformedV3 from "./AdrLocTestsPerformedV3";
import AdrLocAttachmentsV3 from "./AdrLocAttachmentsV3";
import {
  convertNumberToSplitStation,
  convertSplitStationToNumber,
  getCountOfLeadingZeros,
} from "../../_utils/generalUtils";
import AddAlignmentModalV3 from "./AddAlignmentModalV3";
import { SUBLOCATION_WORKPERFORMED_TYPE } from "./constants/adrConstantsV3";
import adrActions from "../../_actions/adrActions";
import "../../_typedefs/globalTypedefs";
import "../../_typedefs/adrV3TypeDefs";
import "draft-js/dist/Draft.css";

class AdrLocWorkPerformedRowV3 extends Component {
  state = {
    /** @type {import("../../_typedefs/adrV3TypeDefs").LocationWorkPerformedRow} */
    formData: {
      workDescription: "",
      workDescriptionDraftState: EditorState.createEmpty(),
      quantity: 0,
      isControllingOperation: false,
      isDisputed: false,
      stationFromOrAt_full: "",
      stationFromOrAt_partial: "",
      stationFromOrAtOffsetValue: "",
      stationTo_full: "",
      stationTo_partial: "",
      stationToOffsetValue: "",
    },
    editorState: EditorState.createEmpty(),
    showLaBlock: false,
    showLmmBlock: false,
    showLtpBlock: false,
    alerts: {
      showAlert_deleteAttachments: false,
    },
    modals: {
      showModal_fileUpload: false,
      showModal_addAlignment: false,
    },
  };

  componentDidMount() {
    const { locationWorkPerformed: locWp } = this.props;

    const stationFromOrAt = locWp.stationFromOrAt
      ? convertNumberToSplitStation(locWp.stationFromOrAt)
      : null;
    const stationTo = locWp.stationTo
      ? convertNumberToSplitStation(locWp.stationTo)
      : null;

    /** @type {import("../../_typedefs/adrV3TypeDefs").LocationWorkPerformedRow} */
    const locWpr = {
      workDescription: locWp.workDescription,
      workDescriptionDraftState: locWp.workDescriptionDraftState,
      quantity: locWp.quantity,
      isControllingOperation: locWp.isControllingOperation,
      isDisputed: locWp.isDisputed,
      stationFromOrAt_full: stationFromOrAt ? stationFromOrAt.fullSta : "",
      stationFromOrAt_partial: stationFromOrAt ? stationFromOrAt.partialSta : "",
      stationFromOrAtOffsetValue: stationFromOrAt
        ? locWp.stationFromOrAtOffsetValue || ""
        : "",
      stationTo_full: stationTo ? stationTo.fullSta : "",
      stationTo_partial: stationTo ? stationTo.partialSta : "",
      stationToOffsetValue: stationTo ? locWp.stationToOffsetValue || "" : "",
    };

    // For backwards compatibility, check if workDesc exists even if workDescDraftState
    // doesn't. If so, create contentState from existing text.
    const editorState = locWp.workDescriptionDraftState
      ? EditorState.createWithContent(
          convertFromRaw(JSON.parse(locWp.workDescriptionDraftState))
        )
      : locWp.workDescription
      ? EditorState.createWithContent(ContentState.createFromText(locWp.workDescription))
      : EditorState.createEmpty();

    this.setState({ formData: locWpr, editorState });
  }

  updateSelectedAlignment = (alignmentLocId) => {
    const { update, markAdrDirty } = this.props;
    if (!alignmentLocId) return;
    update({ sublocationId: alignmentLocId });
    markAdrDirty();
  };

  deleteLocWpr = () => {
    const {
      deleteRow,
      deleteAttachments,
      locationWorkPerformed: locWp,
      markAdrDirty,
    } = this.props;

    // Delete attachments in this row
    let filePathsToDelete = [];
    locWp.attachments.forEach(
      (locWpa) =>
        (filePathsToDelete = [
          ...filePathsToDelete,
          locWpa.filePath,
          locWpa.imageThumbnailFilePath,
        ])
    );
    deleteAttachments(filePathsToDelete);
    deleteRow();
    markAdrDirty();
  };

  handleChange = (e) => {
    let value = e.target.value;
    const name = e.target.name;

    // Live validation: Full Station cannot contain decimals.
    if (["stationFromOrAt_full", "stationTo_full"].includes(name) && value.includes("."))
      value = value.replace(/\./g, "");

    // Live validation: Partial Station cannot be greater than 100.
    if (
      ["stationFromOrAt_partial", "stationTo_partial"].includes(name) &&
      +value >= 100
    ) {
      // Remove any existing decimal points (otherwise value can result in multiple points, e.g.
      // 13.2.44, when existing value was 12.44 and 3 was inserted in the tenth digit.)
      value = value.replace(/\./g, "");
      // Get the position to add back the decimal point. It's not always 2, since
      // there could be leading zeros.
      const clz = getCountOfLeadingZeros(value);
      value = value.slice(0, clz + 2) + "." + value.slice(clz + 2);
    }

    this.setState(
      { formData: { ...this.state.formData, [name]: value } },
      this.props.markAdrDirty
    );
  };

  handleChange_check = (e) => {
    const { update, markAdrDirty } = this.props;
    update({ [e.target.name]: e.target.checked });
    this.setState(
      { formData: { ...this.state.formData, [e.target.name]: e.target.checked } },
      markAdrDirty
    );
  };

  handleChange_draft = (editorState) =>
    this.setState({ editorState }, this.props.markAdrDirty);

  handleBlur_draft = () => {
    const { formData, editorState } = this.state;

    const workDescription = editorState.getCurrentContent().getPlainText();
    const workDescriptionDraftState = JSON.stringify(
      convertToRaw(editorState.getCurrentContent())
    );

    this.setState({
      formData: { ...formData, workDescription, workDescriptionDraftState },
    });
    this.props.update({ workDescription, workDescriptionDraftState });
  };

  handleKeyCommand = (command) => {
    const { editorState } = this.state;
    const newEditorState = RichUtils.handleKeyCommand(editorState, command);
    if (newEditorState) {
      this.handleChange_draft(newEditorState);
      return "handled";
    }
    return "not-handled";
  };

  handleClick_inlineStyle = (inlineStyle) => (e) => {
    e.preventDefault();
    this.setState({
      editorState: RichUtils.toggleInlineStyle(this.state.editorState, inlineStyle),
    });
  };

  handleClick_blockStyle = (blockType) => (e) => {
    e.preventDefault();
    this.setState({
      editorState: RichUtils.toggleBlockType(this.state.editorState, blockType),
    });
  };

  /**
   * Updates offset direction toggler.
   * @param {string} fieldName - Lt/Rt
   */
  handleClick_osDir = (fieldName) => (e) => {
    const { update, markAdrDirty } = this.props;
    update({ [fieldName]: e.target.name });
    markAdrDirty();
  };

  handleChange_select_inspType = (e) => {
    const { update, markAdrDirty } = this.props;
    update({ inspectionType: e.target.value });
    markAdrDirty();
  };

  handleChange_select_location = (selectedLocation) => {
    const { update, markAdrDirty } = this.props;
    // const { formData } = this.state;

    // If the selectedLocation is a 'special' location (e.g. Line or Coordinate),
    // then current locWp.locationId will be -2 (for line/sta, or a different negative value
    // for other types of sublocations), and the sublocation selector will set sublocationId.
    const isSublocation = selectedLocation.value < -1;

    let sublocationWorkPerformedType = SUBLOCATION_WORKPERFORMED_TYPE.NONE; // Init as 0
    // Add other subLoc types as needed
    switch (selectedLocation.value) {
      case -2:
        sublocationWorkPerformedType = SUBLOCATION_WORKPERFORMED_TYPE.ALIGNMENT;
        break;
      default:
        break;
    }

    update({
      locationId: selectedLocation.value, // -2 if line/sta.
      isSublocation,
      sublocationWorkPerformedType,
    });
    markAdrDirty();
  };

  handleChange_select_sublocation = (selectedSublocation) => {
    const { update, markAdrDirty } = this.props;
    // const { formData } = this.state;

    // As above, if a 'special' location is selected in the location selector,
    // then this sublocation selector is used to determine the locationId. However, for
    // any non-special locations with nested sublocations, this is used to set
    // the sublocationId for locWp's nestedSublocations.

    update({ sublocationId: selectedSublocation.value });
    markAdrDirty();
  };

  handleBlur = (e) => {
    const { formData } = this.state;

    // If station field, format station strings prior to dispatching update
    let isStationVal = false;
    let stationVal = "";
    switch (e.target.name) {
      case "stationFromOrAt_full": {
        isStationVal = true;
        stationVal = convertSplitStationToNumber({
          fullSta: e.target.value,
          partialSta: formData.stationFromOrAt_partial,
        });
        break;
      }
      case "stationFromOrAt_partial": {
        isStationVal = true;
        stationVal = convertSplitStationToNumber({
          fullSta: formData.stationFromOrAt_full,
          partialSta: e.target.value,
        });
        break;
      }
      case "stationTo_full": {
        isStationVal = true;
        stationVal = convertSplitStationToNumber({
          fullSta: e.target.value,
          partialSta: formData.stationTo_partial,
        });
        break;
      }
      case "stationTo_partial": {
        isStationVal = true;
        stationVal = convertSplitStationToNumber({
          fullSta: formData.stationTo_full,
          partialSta: e.target.value,
        });
        break;
      }
      default:
        break;
    }

    const targetName = isStationVal ? e.target.name.split("_")[0] : e.target.name;

    this.props.update({
      [targetName]: isStationVal ? stationVal : e.target.value,
    });
  };

  handleClick_addLmm = (e) => {
    const { addMiscMaterials, markAdrDirty } = this.props;
    e.preventDefault();

    addMiscMaterials();
    this.setState({ showLmmBlock: true });
    markAdrDirty();
  };

  handleClick_addLtp = (e) => {
    const { addTestsPerformed, markAdrDirty } = this.props;
    e.preventDefault();

    addTestsPerformed();
    this.setState({ showLtpBlock: true });
    markAdrDirty();
  };

  handleClick_attachFiles = (e) => {
    e.preventDefault();
    this.toggleModal_fileUpload();
  };

  handleClick_delete = (e) => {
    const { locationWorkPerformed: locWp } = this.props;
    e.preventDefault();
    const attachmentsExist = locWp.attachments.length > 0;
    attachmentsExist ? this.toggleAlert_deleteAttachments() : this.deleteLocWpr();
  };

  toggleLaBlock = () => this.setState({ showLaBlock: !this.state.showLaBlock });

  // Provided as custom callback to fileUploadModal
  openLaBlock = () => !this.state.showLaBlock && this.setState({ showLaBlock: true });

  toggleLmmBlock = () => this.setState({ showLmmBlock: !this.state.showLmmBlock });

  toggleLtpBlock = () => this.setState({ showLtpBlock: !this.state.showLtpBlock });

  toggleModal_fileUpload = () =>
    this.setState({
      modals: {
        ...this.state.modals,
        showModal_fileUpload: !this.state.modals.showModal_fileUpload,
      },
    });

  toggleModal_addAlignment = () =>
    this.setState({
      modals: {
        ...this.state.modals,
        showModal_addAlignment: !this.state.modals.showModal_addAlignment,
      },
    });

  toggleAlert_deleteAttachments = () =>
    this.setState({
      alerts: {
        ...this.state.alerts,
        showAlert_deleteAttachments: !this.state.alerts.showAlert_deleteAttachments,
      },
    });

  handleAlert_deleteAttachments = (alertAction) => (e) => {
    e.preventDefault();
    alertAction === "accept" ? this.deleteLocWpr() : this.toggleAlert_deleteAttachments();
  };

  //optionIsDisabled = (option) => {
    //const { locationsToDisable } = this.props;
    //// option.value <= 10 are permanantly disabled options, like separators
    //return (
    //  (locationsToDisable && locationsToDisable.includes(option.value)) ||
    //  option.value <= -10
    //);
  //};

  /**
   * Renders line/sta input box when "Line-Sta" location is chosen.
   * @param {string} idSuffix
   */
  renderSublocBlock_alignment = (idSuffix) => {
    const { status, locationWorkPerformed: locWp, sublocationOptions } = this.props;
    const { formData } = this.state;

    const selectedSublocation =
      sublocationOptions.find((slo) => slo.value === locWp.sublocationId) || null;

    return (
      <div className="locwp-row-subloc-block">
        <label className="mb-0">
          <b>Line-Station</b>
        </label>
        <div className="card w-100 mb-2 bg-lightyellow">
          <div className="card-body p-2">
            <div className="d-md-flex flex-md-row mb-2">
              <div className="locwp-row-subloc-select">
                <label className="my-auto mr-2">
                  <b>Line:</b>
                </label>
                <Select
                  name="sublocationId"
                  id={`subloc_${idSuffix}`}
                  className="w-100 mr-2 my-auto"
                  styles={{ height: "0.1rem" }}
                  value={selectedSublocation}
                  options={sublocationOptions || []}
                  onChange={this.handleChange_select_sublocation}
                  isSearchable={true}
                  isDisabled={status.isReadOnly}
                />
                <button
                  type="button"
                  id={`add_new_align_${idSuffix}`}
                  name="addNewAlign"
                  onClick={this.toggleModal_addAlignment}
                  className="btn btn-secondary"
                  disabled={status.isReadOnly}
                >
                  <FontAwesomeIcon icon="plus" className="fa-fw" />
                </button>
              </div>
            </div>
            <div className="d-lg-flex flex-lg-row">
              <div className="locwp-row-subloc-stationfrom">
                <label className="my-auto mr-1">
                  <b>From/At:</b>
                </label>
                <input
                  name="stationFromOrAt_full"
                  id={`stationFromOrAt_full_${idSuffix}`}
                  type="number"
                  className="form-control form-control-sm my-auto px-1"
                  style={{ width: "15%" }}
                  value={formData.stationFromOrAt_full}
                  onChange={this.handleChange}
                  onBlur={this.handleBlur}
                  disabled={status.isReadOnly || !locWp.sublocationId}
                />
                <label className="my-auto">+</label>
                <input
                  name="stationFromOrAt_partial"
                  id={`stationFromOrAt_partial_${idSuffix}`}
                  type="number"
                  className="form-control form-control-sm my-auto px-1 mr-2"
                  style={{ width: "15%" }}
                  value={formData.stationFromOrAt_partial}
                  onChange={this.handleChange}
                  onBlur={this.handleBlur}
                  disabled={status.isReadOnly || !locWp.sublocationId}
                />
                <label className="my-auto mr-1">
                  (<small>O/S:</small>
                </label>
                <input
                  name="stationFromOrAtOffsetValue"
                  id={`stationFromOrAt_os_${idSuffix}`}
                  type="number"
                  className="form-control form-control-sm my-auto px-1"
                  style={{ width: "12%" }}
                  value={formData.stationFromOrAtOffsetValue}
                  onChange={this.handleChange}
                  onBlur={this.handleBlur}
                  disabled={status.isReadOnly || !locWp.sublocationId}
                />
                <ButtonGroup
                  className="mx-1 my-auto"
                  style={{
                    backgroundColor: !locWp.sublocationId ? "#e9ecef" : "white",
                  }}
                  size="sm"
                >
                  <Button
                    name="Lt"
                    id={`stationFromOrAt_os_lt_${idSuffix}`}
                    style={{ paddingTop: "0.2rem", paddingBottom: "0.2rem" }}
                    color="secondary"
                    outline
                    onClick={this.handleClick_osDir("stationFromOrAtOffsetDirection")}
                    active={locWp.stationFromOrAtOffsetDirection === "Lt"}
                    disabled={status.isReadOnly || !locWp.sublocationId}
                  >
                    Lt
                  </Button>
                  <Button
                    name="Rt"
                    id={`stationFromOrAt_os_rt_${idSuffix}`}
                    style={{ paddingTop: "0.2rem", paddingBottom: "0.2rem" }}
                    color="secondary"
                    outline
                    onClick={this.handleClick_osDir("stationFromOrAtOffsetDirection")}
                    active={locWp.stationFromOrAtOffsetDirection === "Rt"}
                    disabled={status.isReadOnly || !locWp.sublocationId}
                  >
                    Rt
                  </Button>
                </ButtonGroup>
                <label className="my-auto">)</label>
              </div>
              <div className="locwp-row-subloc-stationto">
                <label className="my-auto mr-1">
                  <b>To:</b>
                </label>
                <input
                  name="stationTo_full"
                  id={`stationTo_full_${idSuffix}`}
                  type="number"
                  className="form-control form-control-sm my-auto px-1"
                  style={{ width: "15%" }}
                  value={formData.stationTo_full}
                  onChange={this.handleChange}
                  onBlur={this.handleBlur}
                  disabled={status.isReadOnly || !locWp.sublocationId}
                />
                <label className="my-auto">+</label>
                <input
                  name="stationTo_partial"
                  id={`stationTo_partial_${idSuffix}`}
                  type="number"
                  className="form-control form-control-sm my-auto px-1 mr-2"
                  style={{ width: "15%" }}
                  value={formData.stationTo_partial}
                  onChange={this.handleChange}
                  onBlur={this.handleBlur}
                  disabled={status.isReadOnly || !locWp.sublocationId}
                />
                <label className="my-auto mr-1">
                  (<small>O/S:</small>
                </label>
                <input
                  name="stationToOffsetValue"
                  id={`stationTo_os_${idSuffix}`}
                  type="number"
                  className="form-control form-control-sm my-auto px-1"
                  style={{ width: "12%" }}
                  value={formData.stationToOffsetValue}
                  onChange={this.handleChange}
                  onBlur={this.handleBlur}
                  disabled={status.isReadOnly || !locWp.sublocationId}
                />
                <ButtonGroup
                  className="mx-1 my-auto"
                  style={{
                    backgroundColor: !locWp.sublocationId ? "#e9ecef" : "white",
                  }}
                  size="sm"
                >
                  <Button
                    name="Lt"
                    id={`stationTo_os_lt_${idSuffix}`}
                    style={{ paddingTop: "0.2rem", paddingBottom: "0.2rem" }}
                    color="secondary"
                    outline
                    onClick={this.handleClick_osDir("stationToOffsetDirection")}
                    active={locWp.stationToOffsetDirection === "Lt"}
                    disabled={status.isReadOnly || !locWp.sublocationId}
                  >
                    Lt
                  </Button>
                  <Button
                    name="Rt"
                    id={`stationTo_os_rt_${idSuffix}`}
                    style={{ paddingTop: "0.2rem", paddingBottom: "0.2rem" }}
                    color="secondary"
                    outline
                    onClick={this.handleClick_osDir("stationToOffsetDirection")}
                    active={locWp.stationToOffsetDirection === "Rt"}
                    disabled={status.isReadOnly || !locWp.sublocationId}
                  >
                    Rt
                  </Button>
                </ButtonGroup>
                <label className="my-auto">)</label>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  renderEditor = (idSuffix) => {
    const { status, locationWorkPerformed: locWp } = this.props;
    const { editorState } = this.state;

    const currStyles = Array.from(editorState.getCurrentInlineStyle());
    const currSelection = editorState.getSelection();
    const blockType = editorState
      .getCurrentContent()
      .getBlockForKey(currSelection.getStartKey())
      .getType();

    const isDisabled =
      locWp.locationId === 0 ||
      (locWp.locationId < 0 && !locWp.sublocationId) ||
      status.isReadOnly;

    return (
      <div className="d-flex flex-column locwp-row-work-desc mb-2">
        <label className="mb-0" htmlFor={"locWorkDescription_" + idSuffix}>
          <b>Work Description</b>
        </label>
        <div className="d-flex flex-row adr-editor-toolbar">
          <button
            type="button"
            id={`locWorkDesc_bold_${idSuffix}`}
            name="bold"
            className={`mr-1 btn ${
              currStyles.includes("BOLD") ? "btn-secondary" : "btn-light"
            }`}
            onMouseDown={this.handleClick_inlineStyle("BOLD")}
            onTouchEnd={this.handleClick_inlineStyle("BOLD")}
            disabled={isDisabled}
          >
            <FontAwesomeIcon icon="bold" className="fa-fw" />
          </button>
          <button
            type="button"
            id={`locWorkDesc_italic_${idSuffix}`}
            name="italic"
            className={`mr-1 btn ${
              currStyles.includes("ITALIC") ? "btn-secondary" : "btn-light"
            }`}
            onMouseDown={this.handleClick_inlineStyle("ITALIC")}
            onTouchEnd={this.handleClick_inlineStyle("ITALIC")}
            disabled={isDisabled}
          >
            <FontAwesomeIcon icon="italic" className="fa-fw" />
          </button>
          <button
            type="button"
            id={`locWorkDesc_underline_${idSuffix}`}
            name="underline"
            className={`btn ${
              currStyles.includes("UNDERLINE") ? "btn-secondary" : "btn-light"
            }`}
            onMouseDown={this.handleClick_inlineStyle("UNDERLINE")}
            onTouchEnd={this.handleClick_inlineStyle("UNDERLINE")}
            disabled={isDisabled}
          >
            <FontAwesomeIcon icon="underline" className="fa-fw" />
          </button>
          <span className="mx-1 text-muted">|</span>
          <button
            type="button"
            id={`locWorkDesc_ul_${idSuffix}`}
            name="unordered-list-item"
            className={`mr-1 btn ${
              blockType === "unordered-list-item" ? "btn-secondary" : "btn-light"
            }`}
            onMouseDown={this.handleClick_blockStyle("unordered-list-item")}
            onTouchEnd={this.handleClick_blockStyle("unordered-list-item")}
            disabled={isDisabled}
          >
            <FontAwesomeIcon icon="list-ul" className="fa-fw" />
          </button>
          <button
            type="button"
            id={`locWorkDesc_ol_${idSuffix}`}
            name="ordered-list-item"
            className={`btn ${
              blockType === "ordered-list-item" ? "btn-secondary" : "btn-light"
            }`}
            onMouseDown={this.handleClick_blockStyle("ordered-list-item")}
            onTouchEnd={this.handleClick_blockStyle("ordered-list-item")}
            disabled={isDisabled}
          >
            <FontAwesomeIcon icon="list-ol" className="fa-fw" />
          </button>
        </div>
        <div className={isDisabled ? "editor-disabled" : ""}>
          <Editor
            name="workDescription"
            id={"locWorkDescription_" + idSuffix}
            editorState={editorState}
            onChange={this.handleChange_draft}
            onBlur={this.handleBlur_draft}
            handleKeyCommand={this.handleKeyCommand}
            readOnly={isDisabled}
            spellCheck={true}
            autoCorrect="off"
          />
        </div>
      </div>
    );
  };

  render() {
    const {
      projectId,
      tabId,
      wpTabId,
      locationOptions,
      locationWorkPerformed: locWp,
      jobStamp,
      fileUploadModal,
      status,
      unitOfMeasure,
      markAdrDirty,
    } = this.props;
    const { formData, alerts, modals, showLaBlock, showLmmBlock, showLtpBlock } =
      this.state;

    const idSuffix = `${wpTabId}_${locWp.clientId}`;
    const isDisabled =
      locWp.locationId === 0 ||
      (locWp.locationId < 0 && !locWp.sublocationId) ||
      status.isReadOnly;

    return (
      <React.Fragment>
        <div className="locWorkPerformedRow">
          <div className="d-md-flex flex-md-row">
            <div className="locwp-row-loc-select mb-2">
              <div className="form-row form-group mb-0">
                <label className="mb-0" htmlFor={`loc_${idSuffix}`}>
                  <b>Location</b>
                </label>
                <Select
                  name="locationId"
                  id={`loc_${idSuffix}`}
                  className="w-100"
                  value={locationOptions.find((lo) => lo.value === locWp.locationId)}
                  options={locationOptions || []}
                  onChange={this.handleChange_select_location}
                  //isOptionDisabled={(option) => this.optionIsDisabled(option)}
                  isSearchable={false}
                  isDisabled={status.isReadOnly}
                />
              </div>
              {!locWp.locationId && (
                <span className="error-small">
                  <small>Select a location</small>
                </span>
              )}
            </div>
            <div className="d-md-flex flex-md-column locwp-row-controls">
              {locWp.isSublocation && this.renderSublocBlock_alignment(idSuffix)}
              <div className="d-lg-flex flex-lg-row mb-2">
                {this.renderEditor(idSuffix)}
                <div className="locwp-row-cluster">
                  <div className="form-row form-group mb-2">
                    <div className="col-7 mb-0">
                      <label className="mb-0" htmlFor={`locInspectionTypeId_${idSuffix}`}>
                        <b>Insp. Type</b>
                      </label>
                      <select
                        className="form-control"
                        name="inspectionType"
                        id={`locInspectionTypeId_${idSuffix}`}
                        value={locWp.inspectionType}
                        onChange={this.handleChange_select_inspType}
                        // onBlur={this.handleBlur}
                        disabled={isDisabled}
                      >
                        <option value="0">None</option>
                        <option value="1">Benchmark</option>
                        <option value="2">Intermittent</option>
                        <option value="3">Continuous</option>
                      </select>
                    </div>
                    <div className="col-5">
                      <label className="mb-0" htmlFor={`locQuantity_${idSuffix}`}>
                        <b>Quantity</b>
                      </label>
                      <div className="input-group">
                        <input
                          type="number"
                          name="quantity"
                          id={`locQuantity_${idSuffix}`}
                          value={formData.quantity}
                          onChange={this.handleChange}
                          onBlur={this.handleBlur}
                          className="form-control"
                          disabled={isDisabled}
                        />
                        <div className="input-group-append">
                          <span className="input-group-text">{unitOfMeasure}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="form-row form-group mb-2">
                    <div className="col-12">
                      <div className="custom-control-inline custom-control custom-checkbox float-left mr-1">
                        <input
                          type="checkbox"
                          name="isControllingOperation"
                          id={`isControllingOperation_${idSuffix}`}
                          className="custom-control-input my-auto"
                          disabled={isDisabled}
                          value={formData.isControllingOperation}
                          checked={formData.isControllingOperation}
                          onChange={this.handleChange_check}
                        />
                        <label
                          htmlFor={`isControllingOperation_${idSuffix}`}
                          className="custom-control-label my-auto"
                        >
                          Controlling operation
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="form-row form-group mb-2">
                    <div className="col-12">
                      <div className="custom-control-inline custom-control custom-checkbox float-left mr-1">
                        <input
                          type="checkbox"
                          name="isDisputed"
                          id={`isDisputed_${idSuffix}`}
                          className="custom-control-input my-auto"
                          disabled={isDisabled}
                          value={formData.isDisputed}
                          checked={formData.isDisputed}
                          onChange={this.handleChange_check}
                        />
                        <label
                          htmlFor={`isDisputed_${idSuffix}`}
                          className="custom-control-label my-auto"
                        >
                          Disputed Work
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="form-row float-right mb-0">
                    <div className="form-group ml-1">
                      <UncontrolledButtonDropdown color="secondary" disabled={isDisabled}>
                        <DropdownToggle caret>
                          <FontAwesomeIcon icon="ellipsis-h" />
                        </DropdownToggle>
                        <DropdownMenu right>
                          <DropdownItem
                            id={`addLocMmRowFromMenu_${idSuffix}`}
                            onClick={this.handleClick_addLmm}
                            disabled={
                              isDisabled ||
                              (locWp &&
                                locWp.miscMaterials &&
                                locWp.miscMaterials.length > 0)
                            }
                          >
                            Add Misc. Mat'ls Used
                          </DropdownItem>
                          <DropdownItem
                            id={`addLocLtpRowFromMenu_${idSuffix}`}
                            onClick={this.handleClick_addLtp}
                            disabled={
                              isDisabled ||
                              (locWp &&
                                locWp.testsPerformed &&
                                locWp.testsPerformed.length > 0)
                            }
                          >
                            Add Tests Performed
                          </DropdownItem>
                          <DropdownItem
                            onClick={() => {}}
                            // disabled={
                            //   locationWorkPerformed.locationId === 0 ||
                            //   !status.isReadOnly ||
                            //   (locationWorkPerformed.locationTestsPerformed &&
                            //   locationWorkPerformed.locationTestsPerformed.filter(
                            //     ltp => !ltp.isDeleted
                            //   ).length > 0
                            //     ? true
                            //     : false)
                            // }
                            disabled={true}
                          >
                            Reference METS Data
                          </DropdownItem>
                          {/* <DropdownItem divider />
                      <DropdownItem disabled>Other Options...</DropdownItem> */}
                        </DropdownMenu>
                      </UncontrolledButtonDropdown>
                    </div>
                    <div className="form-group ml-2" id="attach">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        id={"locAttachPhoto_" + idSuffix}
                        onClick={this.handleClick_attachFiles}
                        disabled={isDisabled}
                      >
                        <FontAwesomeIcon icon="paperclip" className="mr-2" />
                        <span className="badge badge-pill badge-light my-auto">
                          {locWp && locWp.attachments.filter((f) => !f.isDeleted).length}
                        </span>
                      </button>
                    </div>
                    <div className="delete form-group ml-2 mr-1">
                      <button
                        type="button"
                        className="btn btn-danger"
                        id={"locDeleteId_" + idSuffix}
                        onClick={this.handleClick_delete}
                        disabled={status.isReadOnly}
                      >
                        <FontAwesomeIcon icon="times" className="fa-fw px-0" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {locWp && locWp.attachments && locWp.attachments.length > 0 && (
                <AdrLocAttachmentsV3
                  locationWorkPerformed={locWp}
                  jobStamp={jobStamp}
                  idSuffix={idSuffix}
                  showLaBlock={showLaBlock}
                  toggleLaBlock={this.toggleLaBlock}
                  toggleModal_fileUpload={this.toggleModal_fileUpload}
                  tabId={tabId}
                  wpTabId={wpTabId}
                  status={status}
                  markAdrDirty={markAdrDirty}
                />
              )}
              {locWp && locWp.miscMaterials && locWp.miscMaterials.length > 0 && (
                <AdrLocMiscMaterialsV3
                  projectId={projectId}
                  tabId={tabId}
                  wpTabId={wpTabId}
                  locationWorkPerformed={locWp}
                  idSuffix={idSuffix}
                  showLmmBlock={showLmmBlock}
                  toggleLmmBlock={this.toggleLmmBlock}
                  status={status}
                  markAdrDirty={markAdrDirty}
                />
              )}
              {locWp && locWp.testsPerformed && locWp.testsPerformed.length > 0 && (
                <AdrLocTestsPerformedV3
                  projectId={projectId}
                  tabId={tabId}
                  wpTabId={wpTabId}
                  locationWorkPerformed={locWp}
                  locationId={locWp.locationId}
                  locWpClientId={locWp.clientId}
                  idSuffix={idSuffix}
                  showLtpBlock={showLtpBlock}
                  toggleLtpBlock={this.toggleLtpBlock}
                  status={status}
                  markAdrDirty={markAdrDirty}
                />
              )}
            </div>
          </div>
          <hr />
        </div>
        {alerts.showAlert_deleteAttachments && (
          <AlertModal
            alertTitle="Delete Attachments?"
            alertTitleClass="bg-warning"
            alertTitleIcon="exclamation-triangle"
            alertText={
              `Deleting this row will also mark the attached files for deletion.` +
              `\nAre you sure you want to delete?`
            }
            acceptCaption={"Yes, Delete"}
            acceptClass={"danger"}
            provideReject={true}
            rejectCaption={"Cancel"}
            rejectClass={"secondary"}
            handleAlertAction={this.handleAlert_deleteAttachments}
            toggleAlertModal={this.toggleAlert_deleteAttachments}
            showAlertModal={alerts.showAlert_deleteAttachments}
          />
        )}
        {modals.showModal_addAlignment && (
          <AddAlignmentModalV3
            projectId={projectId}
            showModal_addAlignment={modals.showModal_addAlignment}
            toggleModal_addAlignment={this.toggleModal_addAlignment}
            updateSelectedAlignment={this.updateSelectedAlignment}
          />
        )}
        {modals.showModal_fileUpload &&
          fileUploadModal({
            tabId,
            wpTabId,
            locationId: locWp.isSublocation ? locWp.sublocationId : locWp.locationId,
            locWpClientId: locWp.clientId,
            gcClientIndex: null,
            targetCategory: 46,
            showModal_fileUpload: modals.showModal_fileUpload,
            toggleModal_fileUpload: this.toggleModal_fileUpload,
            customCallback: this.openLaBlock,
          })}
      </React.Fragment>
    );
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  update: (fieldsToUpdate) =>
    dispatch({
      type: ADR_EDIT.LOCATION_WORKPERFORMED.UPDATE,
      tabId: ownProps.tabId,
      wpTabId: ownProps.wpTabId,
      clientId: ownProps.locationWorkPerformed.clientId,
      fields: fieldsToUpdate,
    }),
  deleteRow: () =>
    dispatch({
      type: ADR_EDIT.LOCATION_WORKPERFORMED.DELETE,
      tabId: ownProps.tabId,
      wpTabId: ownProps.wpTabId,
      clientId: ownProps.locationWorkPerformed.clientId,
    }),
  deleteAttachments: (filePathsToDelete = []) =>
    dispatch(adrActions.deleteAttachments(filePathsToDelete)),
  addMiscMaterials: () =>
    dispatch({
      type: ADR_EDIT.LOCATION_WORKPERFORMED.MISC_MATERIALS.ADD,
      tabId: ownProps.tabId,
      wpTabId: ownProps.wpTabId,
      clientId: ownProps.locationWorkPerformed.clientId,
    }),
  addTestsPerformed: () =>
    dispatch({
      type: ADR_EDIT.LOCATION_WORKPERFORMED.TESTS_PERFORMED.ADD,
      tabId: ownProps.tabId,
      wpTabId: ownProps.wpTabId,
      clientId: ownProps.locationWorkPerformed.clientId,
    }),
});

const mapStateToProps = (state, ownProps) => ({
  jobStamp: state.projects.jobStamps[ownProps.projectId] || null,
});

AdrLocWorkPerformedRowV3.propTypes = {
  projectId: PropTypes.number.isRequired,
  tabId: PropTypes.number,
  wpTabId: PropTypes.number,
  locationId: PropTypes.number,
  locationOptions: PropTypes.array,
  sublocationOptions: PropTypes.array,
  locationsToDisable: PropTypes.array,
  unitOfMeasure: PropTypes.string,
  markAdrDirty: PropTypes.func,
  locationWorkPerformed: PropTypes.object,
};

export default connect(mapStateToProps, mapDispatchToProps)(AdrLocWorkPerformedRowV3);
